import { Fragment, useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Menu, Transition } from '@headlessui/react';
import {
  HomeIcon,
  PaperAirplaneIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  EyeIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  BugAntIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../version';
import ImpersonationBanner from './ImpersonationBanner';
import IssueReporterAnnouncementBanner from './IssueReporterAnnouncementBanner';
import ImpersonationModal from './ImpersonationModal';
import IssueReporterButton from './IssueReporterButton';
import { clsx } from 'clsx';

const IS_STAGING_HOST = window.location.hostname.includes('staging');

const NavItem = ({ item, active, collapsed = false, onClick }) => (
  <Link
    to={item.href}
    onClick={onClick}
    className={clsx(
      'group relative flex items-center gap-x-3 rounded-xl text-sm font-medium transition duration-200',
      collapsed ? 'justify-center p-3' : 'px-3 py-2.5',
      active
        ? 'bg-white/15 text-white shadow-sm'
        : 'text-blue-100 hover:text-white hover:bg-white/10'
    )}
  >
    {active && (
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white rounded-r-full" />
    )}

    <item.icon className={clsx(
      'shrink-0 transition-colors',
      collapsed ? 'size-6' : 'size-5',
      active ? 'text-white' : 'text-blue-200 group-hover:text-white'
    )} />

    {!collapsed && <span className="truncate">{item.name}</span>}

    {collapsed && <span className="sr-only">{item.name}</span>}

    {collapsed && (
      <span aria-hidden="true" className="absolute left-full ml-3 px-2.5 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-visible:opacity-100 group-focus-visible:visible transition-[opacity,visibility] duration-150 whitespace-nowrap z-50 shadow-lg">
        {item.name}
      </span>
    )}
  </Link>
);

const NavSection = ({ title, items, isActive, collapsed = false, onClick }) => {
  if (items.length === 0) return null;

  return (
    <div className="space-y-1">
      {!collapsed && (
        <h3 className="px-3 text-xs font-semibold text-blue-300/70 uppercase tracking-wider">
          {title}
        </h3>
      )}
      <div className="space-y-1">
        {items.map((item) => (
          <NavItem
            key={item.name}
            item={item}
            active={isActive(item.href)}
            collapsed={collapsed}
            onClick={onClick}
          />
        ))}
      </div>
    </div>
  );
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  const { user, logout, isApprover, isCoordinator, isAdmin, isDepartmentManager, isImpersonating, isActuallyAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeChangedRef = useRef(false);

  // Load collapsed state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved) setSidebarCollapsed(JSON.parse(saved));
  }, []);

  // Save collapsed state
  const toggleCollapsed = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  // User navigation - visible to everyone
  const userNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'My Requests', href: '/requests', icon: PaperAirplaneIcon },
    { name: 'Event Attendees', href: '/event-attendees', icon: CalendarDaysIcon },
    { name: 'Reported Issues', href: '/reported-issues', icon: BugAntIcon },
  ];

  // Manager / Travel Lead navigation
  const managerNavigation = [
    { name: 'Approvals', href: '/approvals', icon: ClipboardDocumentCheckIcon },
    { name: 'My Assignees', href: '/my-assignees', icon: UserGroupIcon },
  ].filter(() => isApprover());

  // Department Manager navigation
  const deptManagerNavigation = [
    { name: 'Team Travel', href: '/team-travel', icon: PaperAirplaneIcon },
    { name: 'Team Members', href: '/team-members', icon: UserGroupIcon },
  ].filter(() => isDepartmentManager());

  // Admin navigation
  const adminNavigation = [
    { name: 'All Travel', href: '/all-travel', icon: PaperAirplaneIcon },
    { name: 'Event Travel', href: '/event-travel', icon: CalendarDaysIcon },
    { name: 'Hotel Blocks', href: '/room-blocks', icon: BuildingOfficeIcon },
    { name: 'Hotel Lookup', href: '/hotel-lookup', icon: MagnifyingGlassIcon },
    { name: 'Travelers', href: '/travelers', icon: UserGroupIcon },
    { name: 'Locations', href: '/locations', icon: MapPinIcon },
    { name: 'Financial', href: '/financial', icon: CurrencyDollarIcon, adminOnly: true },
    { name: 'Departments', href: '/departments', icon: BuildingOfficeIcon, adminOnly: true },
    { name: 'System Stats', href: '/system-stats', icon: ChartBarSquareIcon, adminOnly: true },
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, adminOnly: true },
  ].filter((item) => {
    if (item.adminOnly) return isAdmin();
    return isAdmin() || isCoordinator();
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/requests') return 'My Travel Requests';
    if (path.startsWith('/requests/new')) return 'New Travel Request';
    if (path.startsWith('/requests/')) return 'Travel Request Details';
    if (path === '/approvals') return 'Approvals';
    if (path === '/my-assignees') return 'My Assignees';
    if (path === '/team-travel') return 'Team Travel';
    if (path === '/team-members') return 'Team Members';
    if (path === '/all-travel') return 'All Travel';
    if (path === '/room-blocks') return 'Hotel Room Blocks';
    if (path === '/hotel-lookup') return 'Hotel Rate Lookup';
    if (path === '/travelers') return 'Travelers';
    if (path === '/locations') return 'Locations';
    if (path === '/financial') return 'Financial Reports';
    if (path === '/departments') return 'Departments';
    if (path === '/settings') return 'Settings';
    if (path === '/profile') return 'My Profile';
    if (path === '/event-attendees') return 'Event Attendees';
    if (path === '/event-travel') return 'Event Travel';
    if (path === '/reported-issues') return 'Reported Issues';
    if (path.startsWith('/reported-issues/')) return 'Reported Issue';
    if (path === '/changelog') return 'Changelog';
    return '';
  };

  const pageTitle = getPageTitle();

  // SPA route changes announce nothing on their own: sync the tab title and
  // move focus to the content region so AT users hear the new page.
  useEffect(() => {
    document.title = pageTitle ? `${pageTitle} · WRG Travel` : 'WRG Travel';
  }, [pageTitle]);

  useEffect(() => {
    if (!routeChangedRef.current) {
      routeChangedRef.current = true;
      return;
    }
    document.getElementById('main-content')?.focus({ preventScroll: false });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to main content link - visible only on focus for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-wrg-blue-500 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-wrg-blue-300 focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Issue Reporter Announcement Banner (1.6.0 feature highlight) */}
      <IssueReporterAnnouncementBanner />

      {/* Impersonation Modal */}
      <ImpersonationModal
        isOpen={impersonationModalOpen}
        onClose={() => setImpersonationModalOpen(false)}
      />

      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in duration-200 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                {/* Close button */}
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      onClick={() => setSidebarOpen(false)}
                      aria-label="Close navigation"
                    >
                      <XMarkIcon className="size-6 text-white" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-6 overflow-y-auto bg-gradient-to-b from-wrg-blue-500 via-wrg-blue-500 to-wrg-blue-600 px-5 pb-6 shadow-2xl">
                  {/* Logo */}
                  <div className="flex h-16 shrink-0 items-center gap-3 border-b border-white/10 -mx-5 px-5">
                    <img src="/header.png" alt="World Racing Group" className="h-9" width={53} height={36} />
                    <span className="text-lg font-bold text-white tracking-tight">Travel</span>
                  </div>

                  {/* Quick action */}
                  <Link
                    to="/requests/new"
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white font-medium rounded-xl transition duration-200 shadow-sm"
                  >
                    <PlusIcon className="size-5" />
                    New Request
                  </Link>

                  {/* Navigation */}
                  <nav aria-label="Primary" className="flex flex-1 flex-col gap-y-6">
                    <NavSection title="Navigation" items={userNavigation} isActive={isActive} onClick={() => setSidebarOpen(false)} />
                    <NavSection title="Management" items={managerNavigation} isActive={isActive} onClick={() => setSidebarOpen(false)} />
                    <NavSection title="My Team" items={deptManagerNavigation} isActive={isActive} onClick={() => setSidebarOpen(false)} />
                    <NavSection title="Administration" items={adminNavigation} isActive={isActive} onClick={() => setSidebarOpen(false)} />
                  </nav>

                  {/* User section */}
                  <div className="mt-auto space-y-4">
                    <Link
                      to="/profile"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/15 transition-colors"
                    >
                      <div
                        className="size-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm"
                        style={{ backgroundColor: user?.profile_color || '#005581' }}
                      >
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">
                          {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-blue-200 truncate" title={user?.email}>{user?.email}</p>
                      </div>
                    </Link>

                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs text-blue-300/60 text-center">
                        <Link
                          to="/changelog"
                          onClick={() => setSidebarOpen(false)}
                          className="hover:text-white hover:underline transition-colors"
                          title="View changelog"
                        >
                          Version {APP_VERSION}
                        </Link>
                      </p>
                      <p className="text-xs text-blue-300/40 text-center mt-1">
                        &copy; {new Date().getFullYear()} World Racing Group
                      </p>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div
        className={clsx(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-[width] duration-300 ease-smooth',
          sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
        )}
      >
        <div className="flex grow flex-col gap-y-6 overflow-y-auto scrollbar-thin bg-gradient-to-b from-wrg-blue-500 via-wrg-blue-500 to-wrg-blue-600 px-4 pb-4 shadow-xl">
          {/* Logo */}
          <div className={clsx(
            'flex h-16 shrink-0 items-center border-b border-white/10 -mx-4 px-4 gap-3',
            sidebarCollapsed && 'justify-center'
          )}>
            <img src="/header.png" alt="World Racing Group" className="h-9" width={53} height={36} />
            {!sidebarCollapsed && (
              <span className="text-lg font-bold text-white tracking-tight">Travel</span>
            )}
          </div>

          {/* Quick action button */}
          {!sidebarCollapsed ? (
            <Link
              to="/requests/new"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 text-white font-medium rounded-xl transition duration-200 shadow-sm"
            >
              <PlusIcon className="size-5" />
              New Request
            </Link>
          ) : (
            <Link
              to="/requests/new"
              className="group relative flex items-center justify-center p-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition duration-200"
            >
              <PlusIcon className="size-5" />
              <span className="sr-only">New Request</span>
              <span aria-hidden="true" className="absolute left-full ml-3 px-2.5 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-visible:opacity-100 group-focus-visible:visible transition-[opacity,visibility] duration-150 whitespace-nowrap z-50 shadow-lg">
                New Request
              </span>
            </Link>
          )}

          {/* Navigation */}
          <nav aria-label="Primary" className="flex flex-1 flex-col gap-y-6">
            <NavSection title="Navigation" items={userNavigation} isActive={isActive} collapsed={sidebarCollapsed} />
            <NavSection title="Management" items={managerNavigation} isActive={isActive} collapsed={sidebarCollapsed} />
            <NavSection title="My Team" items={deptManagerNavigation} isActive={isActive} collapsed={sidebarCollapsed} />
            <NavSection title="Administration" items={adminNavigation} isActive={isActive} collapsed={sidebarCollapsed} />
          </nav>

          {/* User section - Desktop */}
          <div className="mt-auto space-y-3">
            {!sidebarCollapsed ? (
              <Link
                to="/profile"
                className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/15 transition-colors"
              >
                <div
                  className="size-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm flex-shrink-0"
                  style={{ backgroundColor: user?.profile_color || '#005581' }}
                >
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-blue-200 truncate" title={user?.email}>{user?.email}</p>
                </div>
              </Link>
            ) : (
              <Link
                to="/profile"
                className="group relative flex items-center justify-center p-2"
              >
                <div
                  className="size-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm ring-2 ring-white/20 hover:ring-white/40 transition"
                  style={{ backgroundColor: user?.profile_color || '#005581' }}
                >
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <span className="sr-only">Profile</span>
                <span aria-hidden="true" className="absolute left-full ml-3 px-2.5 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-visible:opacity-100 group-focus-visible:visible transition-[opacity,visibility] duration-150 whitespace-nowrap z-50 shadow-lg">
                  {user?.first_name} {user?.last_name}
                </span>
              </Link>
            )}

            {/* Collapse button */}
            <button
              onClick={toggleCollapsed}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={clsx(
                'w-full flex items-center justify-center gap-2 p-2 rounded-lg text-blue-200 hover:text-white hover:bg-white/10 transition-colors',
                sidebarCollapsed && 'px-2'
              )}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="size-5" />
              ) : (
                <>
                  <ChevronLeftIcon className="size-5" />
                  <span className="text-xs">Collapse</span>
                </>
              )}
            </button>

            {!sidebarCollapsed && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs text-blue-300/50 text-center">
                  <Link
                    to="/changelog"
                    className="hover:text-white hover:underline transition-colors"
                    title="View changelog"
                  >
                    Version {APP_VERSION}
                  </Link>
                </p>
                <p className="text-xs text-blue-300/40 text-center mt-1">
                  &copy; {new Date().getFullYear()} World Racing Group
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={clsx(
        'transition-[padding] duration-300 ease-smooth',
        sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
      )}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white/80 backdrop-blur-md px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          {/* Mobile menu button */}
          <button
            type="button"
            aria-label="Open navigation"
            className="p-2 -m-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="size-6" />
          </button>

          <div className="h-6 w-px bg-gray-200 lg:hidden" />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            {/* Page title */}
            <div className="flex flex-1 items-center gap-3">
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {getPageTitle()}
              </h1>
              {IS_STAGING_HOST && (
                <span
                  className="inline-flex items-center rounded-md bg-amber-400 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-amber-950"
                  title="This is the staging site. Data here is for testing, not production."
                >
                  Staging
                </span>
              )}
            </div>

            <div className="flex items-center gap-x-3 lg:gap-x-4">
              {/* Quick new request button - desktop only */}
              <Link
                to="/requests/new"
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-wrg-blue-500 hover:bg-wrg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
              >
                <PlusIcon className="size-4" />
                New Request
              </Link>

              <IssueReporterButton />

              {/* User menu */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-3 p-1.5 -m-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <span className="sr-only">Open user menu</span>
                  <div
                    className="size-9 rounded-full flex items-center justify-center text-white font-medium text-sm shadow-sm ring-2 ring-white"
                    style={{ backgroundColor: user?.profile_color || '#005581' }}
                  >
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <span className="hidden lg:flex lg:items-center">
                    <span className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <ChevronDownIcon className="ml-2 size-4 text-gray-400" />
                  </span>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 scale-95 translate-y-1"
                  enterTo="opacity-100 scale-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 scale-100 translate-y-0"
                  leaveTo="opacity-0 scale-95 translate-y-1"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate" title={user?.email}>{user?.email}</p>
                    </div>

                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={clsx(
                              'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors',
                              active && 'bg-gray-50'
                            )}
                          >
                            <UserCircleIcon className="size-5 text-gray-400" />
                            Your Profile
                          </Link>
                        )}
                      </Menu.Item>

                      {/* Impersonate User - Admin only */}
                      {isActuallyAdmin() && !isImpersonating && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setImpersonationModalOpen(true)}
                              className={clsx(
                                'flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors',
                                active && 'bg-gray-50'
                              )}
                            >
                              <EyeIcon className="size-5 text-gray-400" />
                              Impersonate User
                            </button>
                          )}
                        </Menu.Item>
                      )}
                    </div>

                    <div className="border-t border-gray-100 py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={clsx(
                              'flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors',
                              active && 'bg-gray-50'
                            )}
                          >
                            <ArrowRightOnRectangleIcon className="size-5 text-gray-400" />
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Page content with animation wrapper */}
        <main id="main-content" tabIndex="-1" className="py-6 animate-fade-in focus:outline-none">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
